import clsx from 'clsx'
import CustomField from 'common/components/CustomField'
import PasswordStrengthMeter from 'common/components/PasswordStrengthMeter'
import {Form, Formik, FormikHelpers, FormikValues} from 'formik'
import {FC, useState} from 'react'
import {Alert} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {RootState} from 'setup'
import {changeUserPassword} from 'setup/redux/Actions/School/User/user'
import * as Yup from 'yup'

type Props = {
  callback?: () => void
  /**
   * Account identifier (email/username) for the hidden autocomplete="username"
   * field. Needed by the forced change-password flow right after login, where
   * the redux user is not saved yet (REDUX_SAVE_LOGIN_INFO is dispatched only
   * after the password change completes). Falls back to the redux user.
   */
  username?: string
}

const ChangePassword: FC<Props> = ({callback, username}) => {
  const intl = useIntl()

  const schema = Yup.object().shape({
    oldPassword: Yup.string()
      .label(intl.formatMessage({id: 'GENERAL.OLD_PASSWORD'}))
      .required(),
    password: Yup.string()
      .label(intl.formatMessage({id: 'GENERAL.NEW_PASSWORD'}))
      .min(8, intl.formatMessage({id: 'GENERAL.PASSWORD_MIN_LENGTH'}))
      .matches(/[A-Z]/, intl.formatMessage({id: 'GENERAL.PASSWORD_UPPERCASE'}))
      .matches(/\d/, intl.formatMessage({id: 'GENERAL.PASSWORD_NUMBER'}))
      .matches(/[^A-Za-z0-9]/, intl.formatMessage({id: 'GENERAL.PASSWORD_SPECIAL'}))
      .required()
      .test(
        'not-same-as-old',
        intl.formatMessage({id: 'PROFILE.NEW_PASSWORD_MUST_DIFFER_FROM_OLD'}),
        function (value) {
          const old = this.parent.oldPassword
          return !value || !old || value !== old
        }
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], intl.formatMessage({id: 'GENERAL.PASSWORDS_MUST_MATCH'}))
      .label(intl.formatMessage({id: 'GENERAL.CONFIRM_PASSWORD'}))
      .required(),
  })

  const initialValues = {
    oldPassword: '',
    password: '',
    confirmPassword: '',
  }

  const [success, setSuccess] = useState('')
  const dispatch = useDispatch()
  const loggedInUser = useSelector<RootState>((state) => state.userReducer.user) as any
  const accountUsername = username || loggedInUser?.email || loggedInUser?.username || ''

  const submit = (values: FormikValues, actions: FormikHelpers<any>) => {
    actions.setSubmitting(true)
    actions.setStatus('')
    setSuccess('')

    const onSuccess = () => {
      actions.setSubmitting(false)
      setSuccess(intl.formatMessage({id: 'PROFILE.PASSWORD_CHANGED_SUCCESSFULLY'}))
      actions.resetForm()
      if (callback) {
        callback()
      }
    }

    const onFailure = (res: any) => {
      actions.setStatus(res.message ?? 'Some error occurred. Please try again.')
      actions.setSubmitting(false)
    }

    dispatch(changeUserPassword(values.oldPassword, values.password, onSuccess, onFailure))
  }

  return (
    <>
      <div className={clsx({card: !callback})}>
        <div className={clsx({'card-body': !callback})}>
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={submit}
            validateOnMount
          >
            {({status, isSubmitting, values, isValid}) => (
              <Form>
                <div className='w-100'>
                  <div className='pb-10 pb-lg-12'>
                    <h2 className='fw-bolder text-dark'>
                      {intl.formatMessage({id: 'PROFILE.CHANGE_PASSWORD'})}
                    </h2>
                  </div>

                  {callback && (
                    <Alert variant='info'>
                      Your password is too old. Please, change your password.
                    </Alert>
                  )}

                  {status && (
                    <div className='mb-lg-15 alert alert-danger'>
                      <div className='alert-text font-weight-bold'>{status}</div>
                    </div>
                  )}

                  {success && (
                    <div className='mb-lg-15 alert alert-success'>
                      <div className='alert-text font-weight-bold'>{success}</div>
                    </div>
                  )}

                  {/* HTML-spec pattern for password-change forms: a
                      type="hidden" autocomplete="username" input anchors the
                      saved credential to this account, so Safari's "Save
                      Password?" sheet prefills the user name. type="hidden"
                      specifically — Safari ignores CSS-hidden text inputs. */}
                  {accountUsername && (
                    <input
                      type='hidden'
                      name='username'
                      autoComplete='username'
                      value={accountUsername}
                    />
                  )}

                  <CustomField
                    type='password'
                    name='oldPassword'
                    label='GENERAL.OLD_PASSWORD'
                    fieldClassName='form-control-lg form-control-solid'
                    required
                    autoComplete='current-password'
                  />

                  <CustomField
                    type='password'
                    name='password'
                    label='GENERAL.NEW_PASSWORD'
                    fieldClassName='form-control-lg form-control-solid'
                    required
                    autoComplete='new-password'
                  />

                  <PasswordStrengthMeter password={values.password} />

                  <CustomField
                    type='password'
                    name='confirmPassword'
                    label='GENERAL.CONFIRM_PASSWORD'
                    fieldClassName='form-control-lg form-control-solid'
                    required
                    autoComplete='new-password'
                    onPaste={(e: React.ClipboardEvent) => {
                      e.preventDefault()
                      toast.warn(intl.formatMessage({id: 'PROFILE.CONFIRM_PASSWORD_NO_PASTE'}))
                    }}
                  />
                </div>

                <div className={clsx({'d-grid': callback})}>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting && (
                      <>
                        <span
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                          aria-hidden='true'
                        />
                        <span className='visually-hidden'>Loading...</span>
                      </>
                    )}
                    {intl.formatMessage({id: 'GENERAL.SAVE'})}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  )
}

export default ChangePassword
